import { getRemitTableJsonData } from "api/remit/remit";
import FullWidthSection from "components/components/FullWidthSection/FullWidthSection";
import PageMeta from "components/components/Meta/PageMeta";
import RemitFiltersForm from "components/components/Remit/RemitFiltersForm/RemitFiltersForm";
import { RevisionHistory } from "components/components/Remit/RemitFiltersForm/remitFilters";
import TableWithErrorHandling from "components/components/TableWithErrorHandling/TableWithErrorHandling";
import { ChartFooter } from "components/dataVisualization/chartComponents/style";
import AdditionalInfoSection from "components/dataVisualization/dataPageComponents/AdditionalInfoSection/AdditionalInfoSection";
import { ExternalDataSource } from "components/dataVisualization/dataPageComponents/AdditionalInfoSection/AdditionalInfoSection";
import PageHeader from "components/dataVisualization/dataPageComponents/PageHeader/PageHeader";
import RemitLandingPageDataExporter from "components/dataVisualization/remit/RemitLandingPageDataExporter";
import RemitLandingPageTable from "components/dataVisualization/remit/RemitLandingPageTable";
import { remitDataCountThreshold, title } from "components/remit/remitConfig";
import {
  getRemitFiltersFromUrlParams,
  mapToRemitRow,
  mridFilter,
  sortRemitData,
} from "components/remit/remitLandingPageHelpers";
import useRequest from "hooks/useRequest";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const Remit: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { request, data, status } = useRequest(getRemitTableJsonData);
  const [mridValue, setMridValue] = useState<string | null>(null);
  const [endpoint, setEndpoint] = useState<string>(
    getRemitFiltersFromUrlParams(searchParams).endpoint
  );
  const [revisionHistoryFilter, setRevisionHistoryFilter] =
    useState<RevisionHistory>(RevisionHistory.Include);

  const fetchData = useCallback(() => {
    const filters = getRemitFiltersFromUrlParams(searchParams);
    request(filters);
    setMridValue(filters.messageID);
    setEndpoint(filters.endpoint);
    setRevisionHistoryFilter(filters.revisionHistory);
  }, [request, searchParams]);

  useEffect(() => fetchData(), [fetchData]);

  const mappedData = data ? mapToRemitRow(data.data) : [];
  const filteredData = mappedData.filter((r) => mridFilter(r, mridValue));
  const sortedData = sortRemitData(filteredData, endpoint);

  const dataIsEmpty = filteredData.length == 0;
  const shouldShowExportButton = !dataIsEmpty;

  return (
    <>
      <PageMeta title={title} />
      <PageMeta title="REMIT" />
      <PageHeader
        title="REMIT"
        pageDescription={
          <>
            <p>
              Market Participants can submit and publish Urgent Market Messages
              (UMMs) on the Elexon Insights Solution, the Inside Information
              Platform (IIP) for the Great Britain electricity market, in
              accordance with their obligations under Regulation (EU) No
              1227/2011 (REMIT) as incorporated into UK law following UK
              departure from the European Union.
            </p>
            <p>
              You must have REMIT submission permission under BSCP38 to submit
              UMMs. For more information please review our{" "}
              <a
                href="https://www.elexon.co.uk/electricity-data/wholesale-data/remit/"
                target="_blank"
                rel="noreferrer"
                className="is-link"
              >
                Submitting REMIT notifications
              </a>{" "}
              page.
            </p>
          </>
        }
        expanderHeading="What you need to know while using this data"
        expanderContent={
          <>
            <p>
              This page displays the latest REMIT messages that have been
              submitted.
            </p>
            <p>
              Use the Timeframe and/or Additional filters to filter your search.
              If looking for a specific message, enter the Message Group ID. If
              no Asset or Participant is selected, you can only view up to 1
              month of data at a time.
            </p>
            <p>
              Further details regarding submitted messages can be viewed by
              clicking on a specific message shown in the table.
            </p>
          </>
        }
      />
      <FullWidthSection className="is-grey-section">
        <RemitFiltersForm
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </FullWidthSection>
      <FullWidthSection className="is-fullwidth">
        <TableWithErrorHandling
          data={sortedData}
          dataFetchStatus={status}
          rerequestData={fetchData}
          rowCountWarningThreshold={remitDataCountThreshold}
        >
          <RemitLandingPageTable
            data={sortedData}
            revisionHistoryFilter={revisionHistoryFilter}
          />
        </TableWithErrorHandling>
      </FullWidthSection>
      {shouldShowExportButton && (
        <ChartFooter rightAligned>
          <RemitLandingPageDataExporter searchParams={searchParams} />
        </ChartFooter>
      )}
      <FullWidthSection>
        This information has been provided by market participants to comply with
        Article 4 of REGULATION (EU) No 1227/2011 OF THE EUROPEAN PARLIAMENT AND
        OF THE COUNCIL of 25 October 2011 on wholesale energy market integrity
        and transparency.
      </FullWidthSection>
      <AdditionalInfoSection
        documentationInformation={{
          table: ["remit"],
        }}
        source={ExternalDataSource.MarketParticipants}
      />
    </>
  );
};

export default Remit;
